import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
	Box,
	CheckOutline,
	HStack,
	IconButton,
	Input,
	PencilOutline,
	Spinner,
	Tag,
	Text,
	Tooltip,
	TrashOutline,
	useToast,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import { useBizyDeleteMemoryMutation, useBizyMemories, useBizyUpdateMemoryMutation } from '../../queries/bizy-memories';
import { BizyMemory } from '../../schemas/bizy-memories';

// ─── Memory Item ─────────────────────────────────────────────────────────────

interface MemoryItemProps {
	memory: BizyMemory;
}

export const MemoryItem: FC<MemoryItemProps> = ({ memory }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(memory.text);
	const inputRef = useRef<HTMLInputElement>(null);
	const toast = useToast();

	const updateMutation = useBizyUpdateMemoryMutation();
	const deleteMutation = useBizyDeleteMemoryMutation();

	useEffect(() => {
		if (isEditing) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [isEditing]);

	const startEditing = () => {
		setEditText(memory.text);
		setIsEditing(true);
	};

	const cancelEditing = () => {
		setEditText(memory.text);
		setIsEditing(false);
	};

	const saveEdit = () => {
		const trimmed = editText.trim();
		setIsEditing(false);

		if (trimmed === memory.text || trimmed.length === 0) return;

		updateMutation.mutate(
			{ id: memory.id, text: trimmed },
			{
				onError: () => {
					toast({ title: 'Failed to update memory', status: 'error', duration: 3000 });
				},
			}
		);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') saveEdit();
		if (e.key === 'Escape') cancelEditing();
	};

	const handleDelete = () => {
		deleteMutation.mutate(memory.id, {
			onError: () => {
				toast({ title: 'Failed to delete memory', status: 'error', duration: 3000 });
			},
		});
	};

	return (
		<Box bg="secondary.bgAlpha" borderRadius="md" px={3} py={2}>
			<HStack spacing={2} mb={1}>
				<Tag size="sm" colorScheme="purple" variant="subtle">
					{memory.category}
				</Tag>
				<Tag size="sm" variant="subtle">
					{memory.source}
				</Tag>
			</HStack>

			{isEditing ? (
				<HStack spacing={1} mt={1}>
					<Input
						ref={inputRef}
						size="sm"
						value={editText}
						onChange={(e: ChangeEvent<HTMLInputElement>) => setEditText(e.target.value)}
						onKeyDown={handleKeyDown}
						onBlur={saveEdit}
						maxLength={256}
					/>
					<IconButton aria-label="Save" icon={<CheckOutline size="xs" />} size="xs" variant="ghost" onClick={saveEdit} />
					<IconButton
						aria-label="Cancel"
						icon={<XOutline size="xs" />}
						size="xs"
						variant="ghost"
						onMouseDown={(e) => e.preventDefault()}
						onClick={cancelEditing}
					/>
				</HStack>
			) : (
				<HStack spacing={1} mt={1} justify="space-between">
					<Text fontSize="sm" color="text.default" flex={1}>
						{memory.text}
					</Text>
					<HStack spacing={0}>
						<Tooltip label="Edit" hasArrow={false}>
							<IconButton aria-label="Edit memory" icon={<PencilOutline size="xs" />} size="xs" variant="ghost" color="secondary.fg" onClick={startEditing} />
						</Tooltip>
						<Tooltip label="Delete" hasArrow={false}>
							<IconButton
								aria-label="Delete memory"
								icon={<TrashOutline size="xs" />}
								size="xs"
								variant="ghost"
								color="secondary.fg"
								isLoading={deleteMutation.isLoading}
								onClick={handleDelete}
							/>
						</Tooltip>
					</HStack>
				</HStack>
			)}
		</Box>
	);
};

// ─── Memories Section ────────────────────────────────────────────────────────

export const MemoriesSection = () => {
	const { data: memories, isLoading } = useBizyMemories();

	return (
		<Box>
			<Text fontSize="sm" fontWeight="semibold" color="text.default" mb={2}>
				Memories
			</Text>
			<Text fontSize="xs" color="text.softer" mb={3}>
				Things Bizy remembers about you. Edit or remove any memory.
			</Text>

			{isLoading ? (
				<HStack justify="center" py={4}>
					<Spinner size="sm" />
				</HStack>
			) : !memories || memories.length === 0 ? (
				<Text fontSize="sm" color="text.softer">
					No memories yet. Bizy will learn about you as you chat.
				</Text>
			) : (
				<VStack spacing={2} align="stretch">
					{memories.map((memory) => (
						<MemoryItem key={memory.id} memory={memory} />
					))}
				</VStack>
			)}
		</Box>
	);
};
